import { DatalayerService } from '@analytics/datalayer';
import { AnalyticsActionTypes, type ToolTrackValues } from '@analytics/models';
import { ToolTrackingUtils } from '@analytics/utils';
import type { OnInit } from '@angular/core';
import { Directive, inject, Input } from '@angular/core';

/**
 * Configuration interface for tool-step tracking.
 *
 */
interface TrackStepModel extends ToolTrackValues {
  extraProperties?: any;
}

/**
 * Angular directive for tracking tool steps.
 * This directive sends analytics events when the directive is loaded into the DOM.
 *
 * Usage:
 * ```html
 * <div [wlTrackStep]="{
 *   toolName: 'Login',
 *   stepName: 'User Details',
 *   step: 1
 * }">
 * ```
 */
@Directive({
  selector: '[wlTrackStep]',
  standalone: true,
})
export class TrackStepDirective implements OnInit {
  readonly #datalayer = inject(DatalayerService);
  readonly #toolTrackingUtils = inject(ToolTrackingUtils);

  /**
   * Default configuration values for tool step tracking.
   */
  readonly #defaultBindingValues: TrackStepModel = {
    type: AnalyticsActionTypes.STEP,
    extraProperties: {},
    step: 0,
  };

  /**
   * Input binding for tool step tracking configuration
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input('wlTrackStep') trackStep: TrackStepModel = this.#defaultBindingValues;

  async ngOnInit() {
    const values = this.#getBindingValues();

    // Create and send tool-step analytics event
    const event = this.#toolTrackingUtils.createToolEvent(
      // We use `trackStep.type` in order to temporarily allow `tool-laststep` events.
      this.trackStep.type ?? AnalyticsActionTypes.STEP,
      this.trackStep.extraProperties ?? {},
      await this.#toolTrackingUtils.getToolMetadata(values)
    );

    this.#datalayer.pushEvent(event);
  }

  /**
   * Gets the merged configuration values (defaults + user provided)
   */
  #getBindingValues(): TrackStepModel {
    return {
      ...this.#defaultBindingValues,
      ...this.trackStep,
    };
  }
}
